/**
 * ADDAPT Agent Chat -- Type Definitions
 *
 * Central type file for the agent streaming chat experience.
 * Matches the backend WebSocket contract defined in UI_AGENT_INTEGRATION.md.
 */

// ---------------------------------------------------------------------------
// Backend WebSocket contract
// ---------------------------------------------------------------------------

/** All event types the backend can send over the WebSocket. */
export type AgentEventType =
  | 'typing'
  | 'agentStart'
  | 'thinking'
  | 'toolUse'
  | 'toolResult'
  | 'delta'
  | 'agentEnd'
  | 'message'
  | 'error';

/** Shape of every WebSocket message received from the backend for agent chat. */
export interface AgentWsMessage {
  type: AgentEventType;
  sessionId?: string;

  /** thinking, delta */
  text?: string;

  /** typing, message, error */
  message?: string;

  /** message -- distinguishes agent responses from basic Bedrock responses */
  source?: 'agent';

  /** toolUse, toolResult */
  toolUseId?: string;

  /** toolUse */
  toolName?: string;

  /** toolUse -- sub-operation within a unified tool (e.g. "CheckAccess", "SearchTables") */
  operationName?: string;

  /** toolUse -- the full parsed input object sent to the tool */
  input?: Record<string, unknown>;

  /** toolResult -- "success" | "error" */
  status?: string;

  /** toolResult -- polymorphic: array, object, or string */
  content?: unknown;

  /** agentEnd */
  stopReason?: string;

  /** message -- summary of all tools used during the turn */
  toolCalls?: ToolCallSummary[];

  /** message */
  timestamp?: string;

  /** message -- pre-built rich parts from backend (Path B forward-compat) */
  structuredParts?: AgentMessagePart[];
}

export interface ToolCallSummary {
  toolUseId: string;
  toolName: string;
  operationName?: string;
  input: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Stream state machine
// ---------------------------------------------------------------------------

/** Phases of the agent streaming state machine. */
export type AgentStreamPhase =
  | 'idle'
  | 'thinking'
  | 'toolRunning'
  | 'streaming'
  | 'complete'
  | 'error';

// ---------------------------------------------------------------------------
// Tool step tracking
// ---------------------------------------------------------------------------

/** Represents a single tool invocation tracked across toolUse -> toolResult. */
export interface ToolStep {
  toolUseId: string;
  toolName: string;
  operationName?: string;
  displayName: string;
  status: 'running' | 'complete' | 'error';
  input?: Record<string, unknown>;
  output?: unknown;
  startTime: number;
  duration?: number;
}

// ---------------------------------------------------------------------------
// Aggregate stream state (exposed by useAgentStream hook)
// ---------------------------------------------------------------------------

export interface AgentStreamState {
  phase: AgentStreamPhase;
  thinkingText: string;
  toolSteps: ToolStep[];
  streamedText: string;
  fullMessage: string | null;
  toolCalls: ToolCallSummary[] | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Agent message parts (used to render finalized agent messages)
// ---------------------------------------------------------------------------

export interface AccessGroup {
  systemName: string;
  tables: AccessTableEntry[];
}

export interface AccessTableEntry {
  name: string;
  status: 'granted' | 'denied' | 'pending' | 'not_onboarded' | 'role_no_access';
  action?: string;
  owner?: string;
  classification?: string;
  checks?: {
    userCanRead?: boolean;
    addaptRoleHasAccess?: boolean;
    addaptIsSubscribed?: boolean;
  };
  issues?: string[];
}

export interface ActionButtonDef {
  label: string;
  style: 'primary' | 'normal';
  action: string;
}

export interface CodeMetadata {
  tables?: string[];
  estRows?: string;
}

export interface AccessFormQuestion {
  id: string;
  label: string;
  type: 'text' | 'select' | 'radio' | 'textarea';
  required: boolean;
  options?: string[];
  value?: string;
  disabled?: boolean;
}

export interface AccessFormData {
  title: string;
  tables: Array<{ name: string; owner: string; classification: string }>;
  consumer: string;
  teamName: string;
  businessJustification: string;
  additionalInformation?: string;
  questions: AccessFormQuestion[];
}

export interface RequirementSection {
  title: string;
  items: string[];
}

export interface ArtifactSection {
  title: string;
  type: 'text' | 'mermaid' | 'codeBlock' | 'table';
  language?: string;
  content: string;
  s3Uri?: string;  // Optional S3 URI for frontend fetching
  headers?: string[];  // CSV table headers
  rows?: string[][];   // CSV table rows
}

export interface JobEntry {
  jobId: string;
  name: string;
  status: 'RUNNING' | 'FAILED' | 'COMPLETE' | 'PENDING' | 'CANCELLED';
  lastRun?: string;
  jobType?: string;
}

export interface SuggestedAction {
  label: string;
  action: string;
  icon?: string;
  /** When set, clicking navigates to this in-app route instead of sending a chat message. */
  url?: string;
}

export type AgentMessagePart =
  | { type: 'thinking'; steps: ToolStep[]; duration?: number }
  | { type: 'text'; content: string }
  | { type: 'codeBlock'; language: string; code: string; metadata?: CodeMetadata }
  | { type: 'mermaid'; content: string }
  | { type: 'accessStatus'; groups: AccessGroup[]; summary: string; degraded?: boolean; degraded_warning?: string }
  | { type: 'actionCard'; title?: string; description: string; buttons: ActionButtonDef[] }
  | {
      type: 'outputPreview';
      columns: string[];
      rows: unknown[][];
      totalEstimate: number;
      queryTime?: string;
    }
  | {
      type: 'notification';
      variant: 'success' | 'warning' | 'error' | 'info';
      title: string;
      description?: string;
      action?: { label: string; action: string };
    }
  | { type: 'form'; formData: AccessFormData }
  | {
      type: 'requirementReview';
      title: string;
      useCaseId?: string;
      objective?: string;
      status?: string;
      sections: RequirementSection[];
      buttons: ActionButtonDef[];
    }
  | {
      type: 'jobDashboard';
      jobs: JobEntry[];
      summary?: string;
      buttons?: ActionButtonDef[];
    }
  | {
      type: 'artifactReview';
      jobId: string;
      jobName?: string;
      generateStatus: string;
      sections: ArtifactSection[];
      buttons: ActionButtonDef[];
    }
  | {
      type: 'suggestedActions';
      actions: SuggestedAction[];
    }
  | {
      type: 'accessRequestGuide';
      deniedTables: string[];
    };

// ---------------------------------------------------------------------------
// Tool name -> human-readable display name mapping
// ---------------------------------------------------------------------------

export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  healthCheck: 'Health Check',
  addaptServiceApi: 'Calling ADDAPT API',
  finqSearch: 'Searching Tables (FinQ)',
  finqGetSchema: 'Getting Table Schema',
  finqGetRelationships: 'Getting Table Relationships',
  fintellaCheckAccess: 'Checking Access (Fintella)',
  fintellaRequestAccess: 'Requesting Access (Fintella)',
  andesCheckAccess: 'Checking Access (Andes)',
  andesRequestAccess: 'Requesting Access (Andes)',
  sqlValidate: 'Validating SQL',
  sqlExecuteSample: 'Executing Sample Query',
  batch_check_access: 'Batch Access Check',
  search_global_insights: 'Searching Insights',
  load_usecase_flow: 'Loading Use-Case Flow',
  check_samples_status: 'Checking Sample Generation',
  check_code_status: 'Checking Code Generation',
};

export const OPERATION_DISPLAY_NAMES: Record<string, string> = {
  CheckAccess: 'Checking Access',
  RequestAccess: 'Requesting Access',
  SearchTables: 'Searching Tables',
  GetSchema: 'Getting Table Schema',
  GetRelationships: 'Getting Table Relationships',
  ValidateSQL: 'Validating SQL',
  ExecuteQuery: 'Executing Query',
  ExecuteSample: 'Executing Sample Query',
  ListJobs: 'Listing Jobs',
  CreateJob: 'Creating Job',
  GetJobStatus: 'Checking Job Status',
  HealthCheck: 'Health Check',
  CreateUseCase: 'Creating Use Case',
  GetUseCase: 'Loading Use Case',
  UpdateUseCase: 'Updating Use Case',
  GenerateCode: 'Generating Code',
  ListUserTables: 'Listing User Tables',
  GetTableInfo: 'Getting Table Info',
  ListAvailableTables: 'Listing Available Tables',
};

/** Return a human-readable label for a tool invocation. */
export function getToolDisplayName(toolName: string, operationName?: string): string {
  if (operationName) {
    const opLabel = OPERATION_DISPLAY_NAMES[operationName];
    if (opLabel) return opLabel;
    return operationName.replace(/([A-Z])/g, ' $1').trim();
  }

  // Strip MCP gateway prefix (e.g. "addapt-tools-gamma___addaptServiceApi" → "addaptServiceApi")
  const shortName = toolName.includes('___') ? toolName.split('___').pop()! : toolName;
  return TOOL_DISPLAY_NAMES[shortName] ?? TOOL_DISPLAY_NAMES[toolName] ?? `Running ${shortName}...`;
}
